import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api";
import { useAuth } from "@/auth";

const TYPE_ICON = {
  score_ready: "fa-trophy",
  eval_pending: "fa-clock",
  supplier_drop: "fa-arrow-trend-down",
  period_closed: "fa-flag-checkered",
  new_content: "fa-graduation-cap",
  doc_expiring: "fa-file-circle-exclamation",
  integration_error: "fa-circle-exclamation",
  delegation_active: "fa-user-shield",
  objective_change_request: "fa-bullseye",
  objective_change_resolved: "fa-circle-check",
};
const TYPE_COLOR = {
  score_ready: "var(--lala-green, #009639)",
  eval_pending: "var(--lala-blue, #0033AD)",
  supplier_drop: "var(--lala-red, #E4002B)",
  period_closed: "#7B3FBF",
  new_content: "var(--lala-blue, #0033AD)",
  doc_expiring: "#D4A437",
  integration_error: "var(--lala-red, #E4002B)",
  delegation_active: "var(--lala-blue, #0033AD)",
  objective_change_request: "#D4A437",
  objective_change_resolved: "var(--lala-blue, #0033AD)",
};

export default function Notifications() {
  const { user } = useAuth();
  const isSupplier = user?.role === "supplier";
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 100 } });
      let list = data.items || [];
      let unreadCount = data.unread || 0;
      // Phase 1 — supplier sees only informational notifications (no operational/action prompts).
      if (isSupplier) {
        const ALLOWED = new Set(["score_ready", "new_content", "period_closed"]);
        const filteredOut = list.filter((n) => !ALLOWED.has(n.type) && !n.read).length;
        list = list.filter((n) => ALLOWED.has(n.type));
        unreadCount = Math.max(0, unreadCount - filteredOut);
      }
      setItems(list);
      setUnread(unreadCount);
    } finally {
      setLoading(false);
    }
  }, [isSupplier]);

  useEffect(() => { load(); }, [load]);

  const markRead = useCallback((id) => {
    api.post(`/notifications/${id}/read`);
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
  }, []);
  const markAllRead = useCallback(() => {
    api.post("/notifications/read-all");
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  }, []);

  return (
    <div className="px-6 py-10 max-w-5xl mx-auto" data-testid="notifications-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-hand text-[var(--lala-blue)] text-2xl mb-1">centro de notificaciones</p>
          <h1 className="text-4xl font-extrabold text-[var(--ink)]">{isSupplier ? "Consulta tus avisos del programa" : "Tus alertas y avisos"}</h1>
          {isSupplier && (
            <p className="text-sm text-[var(--ink-soft)] mt-1 max-w-2xl">
              Lala te notifica cuando tu scorecard se actualiza o hay contenido nuevo en la plataforma. Esta vista es solo informativa.
            </p>
          )}
        </div>
        {unread > 0 && (
          <button
            data-testid="mark-all-read-btn"
            onClick={markAllRead}
            className="px-5 py-2 rounded-full bg-[var(--lala-blue)] text-white font-semibold hover:bg-[var(--lala-blue-deep)] transition"
          >
            Marcar todas como leídas ({unread})
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-20 font-hand text-2xl text-[var(--lala-blue)]">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 bg-white/50 rounded-2xl border-2 border-dashed border-[var(--lala-blue)]/20">
          <i className="fa-solid fa-bell-slash text-6xl text-[var(--lala-blue)]/30 mb-4"></i>
          <p className="text-xl text-[var(--ink-soft)]">No tienes notificaciones aún.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((n) => {
            const icon = TYPE_ICON[n.type] || "fa-bell";
            const color = TYPE_COLOR[n.type] || "var(--lala-blue)";
            const created = new Date(n.created_at).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
            return (
              <div
                key={n.id}
                data-testid={`notif-${n.type}`}
                className={`p-5 rounded-2xl border-2 flex gap-4 transition ${n.read ? "bg-white/40 border-transparent" : "bg-white border-[var(--lala-blue)]/30 shadow-sm"}`}
              >
                <div
                  className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white"
                  style={{ background: color }}
                >
                  <i className={`fa-solid ${icon} text-lg`}></i>
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-4 mb-1">
                    <h3 className={`font-bold text-[var(--ink)] ${n.read ? "" : "text-lg"}`}>{n.title}</h3>
                    <span className="text-xs text-[var(--ink-soft)] whitespace-nowrap">{created}</span>
                  </div>
                  <p className="text-[var(--ink-soft)] text-sm mb-2">{n.body}</p>
                  <div className="flex items-center gap-3">
                    {n.link && (
                      <Link to={n.link} className="text-sm font-semibold text-[var(--lala-blue)] hover:underline">
                        Ver más →
                      </Link>
                    )}
                    {!n.read && (
                      <button
                        onClick={() => markRead(n.id)}
                        className="text-sm text-[var(--ink-soft)] hover:text-[var(--lala-blue)]"
                      >
                        Marcar como leída
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
